import type {
  ClientRunHistoryItem,
  IntegrationRunListItem,
} from "./integrationsBeta";

/**
 * Backend verdicts about a run's client-facing side, read in ONE place so the
 * UI never re-derives them (CAL-5773, CAL-6165).
 */

/**
 * "Still executing" for an INTERNAL history row: the derived client status is
 * RUNNING only for a fresh in-flight run, and it is computed for every run -
 * re-runs included, although a re-run has no client status of its own. Drives
 * disabled actions and polling.
 */
export const isRunExecuting = (
  run: Pick<IntegrationRunListItem, "client_status_derived">,
): boolean => run.client_status_derived === "RUNNING";

/**
 * The same verdict for a CLIENT history row, which carries only what the
 * client sees: there RUNNING is always the derived status (staff cannot set a
 * status while a run is fresh, and a re-run never reaches the client).
 */
export const isClientRunExecuting = (
  run: Pick<ClientRunHistoryItem, "client_status">,
): boolean => run.client_status === "RUNNING";

/**
 * The run the client sees in place of `run`: the run itself, or - for a
 * re-run - the original it serviced; null when the client sees neither.
 */
export const clientFacingRunId = (
  run: Pick<IntegrationRunListItem, "id" | "client_visible" | "original_run">,
): number | null => (run.client_visible ? run.id : run.original_run);

/**
 * A re-run of this run succeeded, yet the client still reads something else:
 * a successful re-run never promotes the original by itself, so a human has
 * to check the result and set the client status.
 */
export const rerunAwaitsConfirmation = (
  run: Pick<IntegrationRunListItem, "client_status" | "latest_rerun_status">,
): boolean =>
  run.latest_rerun_status === "SUCCESS" &&
  run.client_status !== null &&
  run.client_status !== "SUCCESS";
